import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wrench, Clock, CheckCircle, AlertCircle, Calendar, User, ChevronRight, RefreshCw, Plus } from 'lucide-react';
import api from '../api/apiClient';
import PageHeader from '../components/PageHeader';
import { EmptyState } from '../components/design-system/EmptyState';

interface MaintenanceRequest {
  id: number;
  category: string;
  urgency: string;
  status: string;
  displayStatus: string;
  description: string;
  propertyAddress?: string;
  createdAt: string;
  quoteAmount?: number;
  completedAt?: string;
  canReopen?: boolean;
}

interface StatusGroup {
  SUBMITTED: MaintenanceRequest[];
  IN_REVIEW: MaintenanceRequest[];
  ASSIGNED: MaintenanceRequest[];
  SCHEDULED: MaintenanceRequest[];
  IN_PROGRESS: MaintenanceRequest[];
  COMPLETED: MaintenanceRequest[];
}

export default function RenterMaintenanceTracking() {
  const navigate = useNavigate();
  const [byStatus, setByStatus] = useState<StatusGroup | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<keyof StatusGroup>('SUBMITTED');
  const [summary, setSummary] = useState({ total: 0, active: 0, completed: 0 });

  useEffect(() => {
    loadRequests();
    // Refresh every 30 seconds
    const interval = setInterval(loadRequests, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadRequests = async () => {
    try {
      const response = await api.get<{
        requests: MaintenanceRequest[];
        byStatus: StatusGroup;
        summary: { total: number; active: number; completed: number };
      }>('/renter/maintenance/requests');

      setByStatus(response.byStatus);
      setSummary(response.summary);
    } catch (err: any) {
      setError(err.message || 'Failed to load maintenance requests');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'SUBMITTED': return '#F59E0B';
      case 'IN_REVIEW': return '#8B5CF6';
      case 'ASSIGNED': return '#0C4432';
      case 'SCHEDULED': return '#49c5b6';
      case 'IN_PROGRESS': return '#3B82F6';
      case 'COMPLETED': return '#10B981';
      default: return '#666';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'SUBMITTED': return <Clock className="w-5 h-5" />;
      case 'IN_REVIEW': return <AlertCircle className="w-5 h-5" />;
      case 'ASSIGNED': return <User className="w-5 h-5" />;
      case 'SCHEDULED': return <Calendar className="w-5 h-5" />;
      case 'IN_PROGRESS': return <Wrench className="w-5 h-5" />;
      case 'COMPLETED': return <CheckCircle className="w-5 h-5" />;
      default: return <Clock className="w-5 h-5" />;
    }
  };

  const getStatusDescription = (status: string) => {
    switch (status) {
      case 'SUBMITTED': return 'Request received, awaiting review';
      case 'IN_REVIEW': return 'Under review by property management';
      case 'ASSIGNED': return 'Contractor assigned';
      case 'SCHEDULED': return 'Visit scheduled';
      case 'IN_PROGRESS': return 'Work in progress';
      case 'COMPLETED': return 'Work completed';
      default: return '';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderRequestCard = (request: MaintenanceRequest) => (
    <div
      key={request.id}
      onClick={() => navigate(`/maintenance/${request.id}`)}
      className="bg-white rounded-lg shadow p-4 hover:shadow-md transition-shadow cursor-pointer"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div style={{ color: getStatusColor(request.displayStatus) }}>
            {getStatusIcon(request.displayStatus)}
          </div>
          <span className="font-semibold" style={{ color: '#0C4432' }}>
            #{request.id}
          </span>
          <span
            className="px-2 py-1 text-xs rounded-full font-medium"
            style={{
              backgroundColor: request.urgency === 'EMERGENCY' ? '#FEE' : '#F0F4F8',
              color: request.urgency === 'EMERGENCY' ? '#DC2626' : '#0C4432'
            }}
          >
            {request.urgency}
          </span>
        </div>
        <ChevronRight className="w-5 h-5" style={{ color: '#666' }} />
      </div>

      <p className="font-medium mb-1" style={{ color: '#222' }}>{request.category}</p>
      <p className="text-sm mb-2" style={{ color: '#666' }}>
        {request.description}
      </p>

      <div className="flex items-center justify-between text-xs" style={{ color: '#666' }}>
        <span>{request.propertyAddress}</span>
        <span>{formatDate(request.createdAt)}</span>
      </div>

      {request.canReopen && (
        <div className="mt-2 flex items-center gap-2 text-xs" style={{ color: '#49c5b6' }}>
          <RefreshCw className="w-3 h-3" />
          Can reopen within 7 days
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F8F8F8' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#0C4432' }}></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4" style={{ backgroundColor: '#F8F8F8' }}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <PageHeader
          title="My Maintenance Requests"
          subtitle="Track the progress of your maintenance issues"
          icon={<Wrench className="w-8 h-8" />}
          backPath="/dashboard"
        >
          <button
            onClick={() => navigate('/maintenance/new')}
            className="flex items-center gap-2 px-4 py-2 rounded-md text-white font-medium bg-primary hover:bg-primary/90"
          >
            <Plus className="w-5 h-5" />
            New Request
          </button>
        </PageHeader>

        {error && (
          <div className="mb-6 p-4 rounded-md" style={{ backgroundColor: '#FEE', color: '#DF6C4F' }}>
            {error}
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-2">
              <Wrench className="w-6 h-6" style={{ color: '#0C4432' }} />
              <span className="text-2xl font-bold" style={{ color: '#0C4432' }}>
                {summary.total}
              </span>
            </div>
            <p className="text-sm font-medium" style={{ color: '#666' }}>Total Requests</p>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-2">
              <Clock className="w-6 h-6" style={{ color: '#F59E0B' }} />
              <span className="text-2xl font-bold" style={{ color: '#0C4432' }}>
                {summary.active}
              </span>
            </div>
            <p className="text-sm font-medium" style={{ color: '#666' }}>Active Requests</p>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="w-6 h-6" style={{ color: '#10B981' }} />
              <span className="text-2xl font-bold" style={{ color: '#0C4432' }}>
                {summary.completed}
              </span>
            </div>
            <p className="text-sm font-medium" style={{ color: '#666' }}>Completed</p>
          </div>
        </div>

        {/* Status Tabs */}
        <div className="bg-white rounded-lg shadow">
          <div className="border-b overflow-x-auto">
            <div className="flex">
              {(['SUBMITTED', 'IN_REVIEW', 'ASSIGNED', 'SCHEDULED', 'IN_PROGRESS', 'COMPLETED'] as const).map(status => (
                <button
                  key={status}
                  onClick={() => setActiveTab(status)}
                  className={`px-4 py-3 font-medium whitespace-nowrap border-b-2 transition-colors ${
                    activeTab === status
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span className="hidden sm:inline">{status.replace(/_/g, ' ')}</span>
                    <span className="sm:hidden">{status.split('_')[0]}</span>
                    {byStatus && (
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        activeTab === status ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {byStatus[status]?.length || 0}
                      </span>
                    )}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="p-6">
            {/* Status Description */}
            <div className="mb-4 p-3 rounded-md bg-gray-50 border border-gray-200">
              <p className="text-sm text-gray-700">
                <strong>{activeTab.replace(/_/g, ' ')}:</strong> {getStatusDescription(activeTab)}
              </p>
            </div>

            {/* Request List */}
            {byStatus && byStatus[activeTab].length === 0 ? (
              <EmptyState
                icon={<Wrench size={36} />}
                title="No requests here"
                description={`You have no ${activeTab.replace(/_/g, ' ').toLowerCase()} maintenance requests.`}
                action={
                  activeTab === 'SUBMITTED' ? (
                    <button
                      onClick={() => navigate('/maintenance/new')}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-forest-900 text-white rounded-10 text-sm font-medium hover:bg-forest-800 transition-colors"
                    >
                      <Plus size={15} />
                      Submit a Request
                    </button>
                  ) : undefined
                }
              />
            ) : (
              <div className="space-y-4">
                {byStatus && byStatus[activeTab].map(renderRequestCard)}
              </div>
            )}
          </div>
        </div>

        {/* Help Text */}
        <div className="mt-6 p-4 rounded-md" style={{ backgroundColor: '#E8F5F3' }}>
          <p className="text-sm" style={{ color: '#0C4432' }}>
            <strong>Note:</strong> You can reopen a completed request within 7 days if the issue persists.
            Simply view the request details and click "Reopen Request".
          </p>
        </div>
      </div>
    </div>
  );
}

